import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Building2, Globe, Instagram, Loader2, Mail, MapPin, MessageCircle, Phone, Send, StickyNote, Target, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DuoClairLogo } from "@/components/DuoClairLogo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUSES, STATUS_STYLES, fmtDate } from "@/pages/admin/AdminDashboard";

const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api
      .get(`/admin/leads/${id}`)
      .then((r) => setLead(r.data))
      .catch(() => {
        toast.error("Lead non trovato");
        navigate("/admin");
      });
  }, [id, navigate]);

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status });
      setLead(data);
      toast.success(`Stato aggiornato: ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/leads/${id}/notes`, { text: note });
      setLead(data);
      setNote("");
      toast.success("Nota salvata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNote(false);
    }
  };

  const deleteLead = async () => {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead eliminato");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!lead) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center" data-testid="lead-detail-loading">
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
      </div>
    );
  }

  const contact = lead.contact_info;
  const digits = contact.replace(/[^\d+]/g, "");
  const waNumber = digits.replace(/^\+/, "").replace(/^00/, "");
  const contactLinks =
    lead.preferred_contact_method === "Email"
      ? [{ href: `mailto:${contact}`, label: "Email", icon: Mail }]
      : lead.preferred_contact_method === "WhatsApp"
        ? [{ href: `https://wa.me/${waNumber}`, label: "WhatsApp", icon: MessageCircle }]
        : [{ href: `tel:${digits}`, label: "Chiama", icon: Phone }];

  const fields = [
    { icon: Building2, label: "Settore", value: lead.industry },
    { icon: MapPin, label: "Città", value: lead.city },
    { icon: Target, label: "Obiettivo principale", value: lead.main_objective },
    { icon: lead.preferred_contact_method === "Email" ? Mail : lead.preferred_contact_method === "WhatsApp" ? MessageCircle : Phone, label: `Contatto preferito (${lead.preferred_contact_method})`, value: contact },
  ];

  return (
    <div className="min-h-screen bg-obsidian" data-testid="lead-detail-page">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-obsidian/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <DuoClairLogo compact />
          <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-gold transition-colors" data-testid="back-to-dashboard-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Tutti i lead
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-10 space-y-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl text-slate-100 tracking-tight" data-testid="lead-detail-name">
                {lead.business_name}
              </h1>
              <p className="text-sm text-zinc-500 mt-2">
                Ricevuto il {fmtDateTime(lead.created_at)} · Fonte: <span className="text-gold/80">{lead.source}</span>
              </p>
            </div>
            <Select value={lead.status} onValueChange={changeStatus}>
              <SelectTrigger className={`w-[180px] h-11 rounded-xl border text-sm ${STATUS_STYLES[lead.status]} bg-white/[0.04]`} data-testid="lead-detail-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-coal border-white/10">
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} className="text-sm text-slate-200 focus:bg-gold/10 focus:text-gold-light" data-testid={`detail-status-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="luxury-card p-6 sm:p-8 space-y-6"
            data-testid="lead-detail-dossier"
          >
            <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold/80">Scheda attività</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {fields.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg border border-gold/25 bg-gold/5 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-gold" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</p>
                    <p className="text-sm text-slate-200 break-words">{value}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg border border-gold/25 bg-gold/5 flex items-center justify-center shrink-0">
                  <Instagram className="w-4 h-4 text-gold" />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Instagram</p>
                  {lead.instagram_url ? (
                    <a href={lead.instagram_url} target="_blank" rel="noreferrer" className="text-sm text-gold-light hover:underline break-all" data-testid="lead-instagram-link">
                      {lead.instagram_url}
                    </a>
                  ) : (
                    <p className="text-sm text-zinc-600 italic">Non indicato</p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg border border-gold/25 bg-gold/5 flex items-center justify-center shrink-0">
                  <Globe className="w-4 h-4 text-gold" />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Sito / Google Business</p>
                  {lead.website_url ? (
                    <a href={lead.website_url} target="_blank" rel="noreferrer" className="text-sm text-gold-light hover:underline break-all" data-testid="lead-website-link">
                      {lead.website_url}
                    </a>
                  ) : (
                    <p className="text-sm text-zinc-600 italic">Non indicato</p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-white/5">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Contatta ora</p>
              <div className="flex flex-wrap gap-3">
                {contactLinks.map(({ href, label, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`contact-action-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5 text-sm text-gold-light hover:bg-gold/20 transition-all duration-300"
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </a>
                ))}
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="luxury-card p-6" data-testid="lead-notes-section">
              <div className="flex items-center gap-2 mb-4">
                <StickyNote className="w-4 h-4 text-gold" />
                <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold/80">Note private</h2>
              </div>
              <div className="space-y-3 mb-5 max-h-72 overflow-y-auto pr-1" data-testid="notes-list">
                {lead.notes.length === 0 ? (
                  <p className="text-sm text-zinc-600 italic">Nessuna nota ancora. Annota qui i risultati dell'analisi.</p>
                ) : (
                  [...lead.notes].reverse().map((n) => (
                    <div key={n.id} className="rounded-xl border border-white/8 bg-white/[0.03] p-3.5" data-testid={`note-${n.id}`}>
                      <p className="text-sm text-zinc-200 whitespace-pre-wrap">{n.text}</p>
                      <p className="font-mono text-[10px] text-zinc-600 mt-2">{fmtDateTime(n.created_at)}</p>
                    </div>
                  ))
                )}
              </div>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Es. Recensioni Google scarse (3.2★), sito assente, Instagram attivo ma senza CTA…"
                className="bg-white/[0.04] border-white/10 text-slate-100 placeholder:text-zinc-600 rounded-xl min-h-[90px] focus-visible:ring-1 focus-visible:ring-gold/60"
                data-testid="note-input"
              />
              <Button
                onClick={addNote}
                disabled={savingNote || !note.trim()}
                data-testid="note-save-button"
                className="mt-3 w-full h-11 rounded-xl bg-gradient-to-r from-gold-dark via-gold to-gold-light text-black font-semibold hover:opacity-95 disabled:opacity-40"
              >
                {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Salva nota</span>}
              </Button>
            </div>

            <div className="luxury-card p-6">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Zona pericolosa</h2>
              {!confirmDelete ? (
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="lead-delete-button"
                  className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 w-full justify-start"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Elimina questo lead
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button onClick={deleteLead} data-testid="lead-delete-confirm-button" className="flex-1 bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/30">
                    Conferma eliminazione
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmDelete(false)} data-testid="lead-delete-cancel-button" className="text-zinc-400">
                    Annulla
                  </Button>
                </div>
              )}
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
}
